<template>
    <order-summary-layout
        :total-price="totalPrice"
        :product-store="productStore"
        :extra-prices="extraPrices"
        :search-request="searchRequest"
    >
        <template v-slot>
            <div class="d-flex">
                <v-img
                    max-width="150"
                    class="me-3"
                    tile
                    :src="$filters.imageUrl(image, 600)"
                    :lazy-src="$filters.srcHost('/placeholder.png')"
                    @error="onImgError"
                >
                    <template v-slot:placeholder>
                        <v-row v-show="!imgError && image" class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="grey-lighten-5" />
                        </v-row>
                    </template>
                </v-img>
                <div>
                    <div :title="info.name" class="text-primary font-weight-medium text-subtitle-1">
                        {{ info.name }}
                    </div>
                    <supplier-name-label v-if="isTO1" :supplier-name="product.supplierName" class="mb-2" />
                    <div v-if="info.shortDescription" class="mb-4 text-body-2" v-html="info.shortDescription" />
                </div>
            </div>
            <slot name="summary" />
            <v-divider class="mb-2" />
            <div>
                <div class="d-flex align-center font-weight-bold cursor-pointer mb-1" @click="offerInfo()">
                    <v-icon size="small" start>mdi-information-outline</v-icon>
                    <span>
                        {{ offer.serviceName }}
                    </span>
                    <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
                </div>
                <div v-if="offer.available" class="d-flex align-center text-caption">
                    <v-icon :title="$t('available')" start size="small" class="text-success">
                        mdi-checkbox-marked-circle
                    </v-icon>
                    <span class="text-success" v-text="$t('available')" />
                </div>
                <div v-else class="text-caption d-flex align-center">
                    <v-icon :title="$t('on_request')" start size="small" class="text-warning">mdi-alert-circle</v-icon>
                    <span class="text-warning" v-text="$t('on_request')" />
                </div>
                <cruise-meal-type-label v-if="offer.mealType" :offer="offer" />
                <conditions-label
                    :offer-key="offer.offerKey"
                    :price="offer.price"
                    class="text-caption"
                    @conditions="showConditionsInfo"
                />
                <div v-if="offer.tariffName" class="d-flex align-center text-caption">
                    <v-icon start size="small" class="text-info">mdi-tag-text</v-icon>
                    <span>{{ offer.tariffName }}</span>
                </div>
                <div class="mt-1 font-weight-bold">
                    <span>
                        {{ $filters.price(offerPrice) }}
                    </span>
                </div>
                <extra-services-summary
                    v-if="extraServices && additionalOptions"
                    :extra-services="extraServices"
                    :additional-options="additionalOptions"
                />
                <add-ons-summary-list :selected-add-ons="selectedAddOns" />
            </div>
        </template>
    </order-summary-layout>
</template>

<script lang="ts">
    import {Component, Prop} from 'vue-facing-decorator'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import ConditionsLabel from '@/components/snippets/ConditionsLabel.vue'
    import OrderSummaryLayout from '@/components/booking/OrderSummaryLayout.vue'
    import ExtraServicesSummary from '@/components/booking/snippets/ExtraServicesSummary.vue'
    import {EventBus, SHOW_CRUISE_CONDITIONS_INFO, SHOW_CRUISE_OFFER_INFO_MODAL} from '@/utils/event-bus'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import CruiseMealTypeLabel from '@/components/hotels/snippets/CruiseMealTypeLabel.vue'
    // import AddOnsSummaryList from '@/components/booking/addOns/AddOnsSummaryList.vue'
    import {clone} from '@/utils/helpers'
    import {OrderSummaryBase} from '@/components/booking/OrderSummaryBase.vue'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'

    @Component({
        components: {
            ProductOfferTipsDialog,
            CruiseMealTypeLabel,
            AddOnsSummaryList: defineAsyncComponent(() => import('@/components/booking/addOns/AddOnsSummaryList.vue')),
            SupplierNameLabel,
            ExtraServicesSummary,
            OrderSummaryLayout,
            ConditionsModal,
            ConditionsLabel,
        },
    })
    export class CruiseOrderSummary extends OrderSummaryBase {
        @Prop({required: true}) offer
        @Prop({required: true}) info
        @Prop({default: true}) prebooked
        @Prop({required: true, type: Array}) selectedAddOns

        imgError = false

        onImgError() {
            this.imgError = true
            return false
        }

        showConditionsInfo() {
            EventBus.$emit(SHOW_CRUISE_CONDITIONS_INFO, this.offer)
        }

        offerInfo() {
            EventBus.$emit(SHOW_CRUISE_OFFER_INFO_MODAL, {offer: this.offer})
        }

        get offerPrice() {
            const price =
                this.prebooked && this.offer?.currentPrice ? clone(this.offer.currentPrice) : clone(this.offer.price)
            const mandatory =
                cruisesStore.bookingAdditionalOptions?.extraServices?.filter(extraService => extraService.mandatory) ||
                []

            if (mandatory.length) {
                mandatory.forEach(item => {
                    price.amount -= item.salesTerms.find(el => el.type === 'CLIENT').price.amount
                    price.originalAmount -= item.salesTerms.find(el => el.type === 'CLIENT').originalAmount
                })
            }

            return price
        }

        get productStore() {
            return cruisesStore
        }

        get image() {
            if (!this.info.images) return null
            let main = this.info.images.find(image => image.mainImage)
            if (!main && this.info.images.length > 0) {
                main = this.info.images[0]
            }
            return main && main.url ? main.url : null
        }

        get isTO1() {
            return authStore.isTO1
        }

        get orderTotalPrice() {
            return cruisesStore.orderTotalPrice(
                this.offer,
                this.prebooked,
                this.selectedAddOns,
                this.prepareBookResponse
            )
        }

        get basketItem() {
            return this.productStore.basketItem(this.$route.query.offerKey)
        }

        get product() {
            return this.basketItem.product
        }
    }

    export default CruiseOrderSummary
</script>
